import axios from 'axios';
import { getCookie, removeCookie, setCookie } from '@/helpers/cookie';
import swal from 'sweetalert2';
import router from '@/router';
// Importar MSAL diretamente
import msalInstance from '@/plugins/msal/index.js';
import { loginRequest } from '@/plugins/msal/index.js';

const instance = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
});

instance.interceptors.request.use(
  (config) => {
    // Só define Authorization se não foi definido explicitamente
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${getCookie('token')}`;
    }

    if ((config.url || '').match(/^\//))
      config.headers['token'] = getCookie('token');
    else delete config.headers['token'];

    config.headers['Cache-Control'] = 'no-cache';
    config.headers['Pragma'] = 'no-cache';
    config.headers['Expires'] = '0';

    return config;
  },
  (err) => {
    return Promise.reject(err);
  }
);

// Flag para evitar múltiplos refreshs simultâneos
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

instance.interceptors.response.use(
  (res) => {
    const { data } = res;
    if (!data) return false;

    return res;
  },
  async (err) => {
    const originalRequest = err.config;

    // Evitar loop infinito - se a requisição já foi refeita, não tentar refresh novamente
    if (originalRequest._retry) {
      return Promise.reject(err);
    }

    if (err.response?.status === 401) {
      // Se já está tentando refresh, adicionar à fila
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            if ((originalRequest.url || '').match(/^\//)) {
              originalRequest.headers['token'] = token;
            }
            return instance(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      isRefreshing = true;
      originalRequest._retry = true;

      // Tentar fazer refresh token do MSAL antes de deslogar
      try {
        // Garantir que o MSAL está inicializado
        await msalInstance.initialize();

        const accounts = msalInstance.getAllAccounts();

        if (accounts && accounts.length > 0) {
          // Tentar obter novo token do MSAL
          const silentRequest = {
            ...loginRequest,
            account: accounts[0],
          };

          const tokenResponse = await msalInstance.acquireTokenSilent(silentRequest);
          const newAccessToken = tokenResponse.accessToken;

          // Obter novo token do backend usando o token MSAL renovado
          const { data } = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/Login/AdditionalInformation`, {
            headers: {
              'Authorization': `Bearer ${newAccessToken}`
            }
          });

          // Atualizar cookie com novo token
          setCookie('token', data.token, 23);
          if (data.userId) {
            setCookie('userId', data.userId);
          }

          // Atualizar header da requisição original
          originalRequest.headers.Authorization = `Bearer ${data.token}`;
          if ((originalRequest.url || '').match(/^\//)) {
            originalRequest.headers['token'] = data.token;
          }

          // Processar fila de requisições pendentes
          processQueue(null, data.token);
          isRefreshing = false;

          // Reenviar requisição original
          return instance(originalRequest);
        } else {
          // Não há conta MSAL, deslogar
          throw new Error('Nenhuma conta MSAL encontrada');
        }
      } catch (refreshError) {
        // Refresh token falhou, deslogar usuário
        processQueue(refreshError, null);
        isRefreshing = false;

        removeCookie('token');
        removeCookie('tenantId');
        removeCookie('userId');
        setCookie('olp_csl_expired_token', true, 96);

        window.location.href = router.resolve({ name: 'login' }).href;

        return Promise.reject(refreshError);
      }
    }

    if (!err.response) {
      swal.fire({
        title: 'Oops!',
        html: 'Erro ao tentar se conectar com o servidor. <br/> Por favor, verifique se você está conectado a internet e se está conectado a VPN.',
        icon: 'error',
        confirmButtonText: 'Ok',
      });

      return Promise.reject({
        ...err,
        response: {
          data: {
            message:
              'Erro ao tentar se conectar com o servidor. Por favor, verifique se você está conectado a internet e se está conectado a VPN.',
          },
        },
      });
    }
    return Promise.reject(err);
  }
);

export default instance;
